// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  MediumG,
  MediumHr,
  Roboto,
  SmallText,
  Spacer,
  StrongG,
  StrongHr,
  SubTitle,
  WeakG
} from '@/atoms/global'
import {
  Description,
  HeroTitle,
  MainTitle,
  SupDescription,
  SupLabel
} from '@/atoms/global/texts'
import NitButton from '@/atoms/NitButton/NitButton.vue'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { isEmbed, isPreview } from '@/helpers/displayType'
import { onAuthStateChanged } from 'firebase/auth'
import { createPinia } from 'pinia'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { addIntegration, captureException, Integration } from '@sentry/core'
import {
  browserTracingIntegration,
  createSentryPiniaPlugin,
  init,
  setTag,
  setUser,
  vueIntegration
} from '@sentry/vue'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import mixpanel from 'mixpanel-browser'
import { v4 as uuidv4 } from 'uuid'
import { App, createApp } from 'vue'
import NitteApp from './App.vue'
import { clickOutside, keydown } from './directives'
import { auth } from './firebase'
import { updateAnalyticsUser } from './helpers/analytics'
import { bootClack } from './helpers/clack'
import { isDesktop } from './helpers/detectDevice'
import { PiniaFirestoreSync } from './piniaFirestoreSync'
import router from './router'
// 既存のService Workerを削除（一時的な措置）
// TODO: 数週間後に削除可能
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister()
    })
  })
}

// 1. 先にSentryを初期化（Vueインテグレーションを除外）
// session replay は ~135KB と大きく sample rate も 0.01〜1% で利用率が低いため一旦 OFF。
// 本格運用が必要になった時点で `replayIntegration` と `replays{Session,OnError}SampleRate` を再導入する。
init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  // Web Vitals（LCP / INP）取得のためパフォーマンス計測を有効化。
  // Spans の月間消費を見てから後で 0.3 程度まで上げる想定なので、まずは 0.1。
  tracesSampleRate: 0.1,
  // browserTracingIntegration はデフォルト統合に含まれないので明示的に足す。
  // これが無いと tracesSampleRate を設定しても pageload / navigation の
  // トランザクションも Web Vitals（LCP / INP）も一切送られない（分母が取れない）。
  integrations: (integrations: Integration[]) => [
    ...integrations.filter(
      (integration: Integration) => integration.name !== 'Vue'
    ),
    browserTracingIntegration({ router })
  ]
})

// 未ログインユーザーも一意に識別するため、localStorage に匿名ID（UUID）を持たせ、
// init 直後に setUser({ id: 匿名ID }) する。これが無いと未ログインが 1 ユーザーに
// 丸められ、セッション数に対して user 数が極端に少なくなっていた
// （セッション 11,154 に対し user 232）。ログイン確定後は onAuthStateChanged で uid に上書きする。
const ANON_ID_KEY = 'nitte_anonymous_id'
const getOrCreateAnonymousId = (): string => {
  try {
    const stored = localStorage.getItem(ANON_ID_KEY)
    if (stored) {
      return stored
    }
    const created = uuidv4()
    localStorage.setItem(ANON_ID_KEY, created)
    return created
  } catch {
    // プライバシーモード等で localStorage が使えない場合はメモリ上のIDにフォールバックする
    return uuidv4()
  }
}
const anonymousId = getOrCreateAnonymousId()
setUser({ id: anonymousId })

// auth タグ（guest / member）は「体験が悪いユーザーの割合」を未ログイン別に出すための
// 分割キーで、分母は Sentry のトランザクション（pageload）から取る。ところが pageload は
// onAuthStateChanged より先に終わることがあり、init 時点で guest と決め打ちすると
// ログイン済みユーザーの pageload まで guest に混ざって分母が歪む。
// Firebase の認証解決は非同期なので、直前の認証状態を localStorage に控えておき、
// init 時はそれを初期値に使う（2回目以降の訪問は最初のイベントから正しく分かれる）。
// 認証が確定したら onAuthStateChanged が上書きする。
const AUTH_HINT_KEY = 'nitte_auth_hint'
type AuthKind = 'guest' | 'member'

const readAuthHint = (): AuthKind => {
  try {
    return localStorage.getItem(AUTH_HINT_KEY) === 'member' ? 'member' : 'guest'
  } catch {
    return 'guest'
  }
}

const applyAuthKind = (kind: AuthKind): void => {
  setTag('auth', kind)
  try {
    localStorage.setItem(AUTH_HINT_KEY, kind)
  } catch {
    // localStorage が使えない環境ではヒントを保存できないだけで、タグ自体は付く
  }
}

setTag('auth', readAuthHint())

// session replay (record_sessions_percent + record_mask_*) を OFF にすることで
// mixpanel-browser の recorder code (~300 KB) が tree-shake で bundle から落ちる。
// 必要になった時点で recording 関連オプションを再追加する。
//
// autocapture は本ビルド（core SDK のみ）では利用できず、
// "This build of Mixpanel only includes core SDK functionality" のエラーを大量発生させ
// （直近14日で 4,252件 / 700ユーザー = 全エラーの78%）Sentry の計測ノイズになっていたため撤去する。
// helpers/mixpanel.ts のオンボーディングイベント（明示 track）は代替が無いため当面残す。
//
// staging 等トークン未設定の環境では init せず、Mixpanel 利用も no-op にする。
// 未初期化のまま identify() を呼ぶと `Cannot read properties of undefined (reading
// 'before_identify')` で落ちるため、トークンの有無でガードする。
const mixpanelToken = import.meta.env.VITE_APP_MIXPANEL_TOKEN
if (mixpanelToken) {
  mixpanel.init(mixpanelToken)
}

let app: App<Element>
// NOTE: firebase authがsessionを引いてからアプリを作るようにする
onAuthStateChanged(auth, async (user) => {
  // Sentry のユーザー識別を更新する。ログインが確定したら匿名IDから uid へ上書きし、
  // 未ログインに戻ったら匿名IDに戻す。auth タグも member / guest で更新する。
  // 二重 mount ガードより前に置き、認証状態が変わるたびに必ず反映されるようにする。
  if (user) {
    setUser({ id: user.uid, email: user.email ?? undefined })
  } else {
    setUser({ id: anonymousId })
  }
  // 次回訪問の init 時に初期値として使えるよう、確定した状態をヒントとして残す
  applyAuthKind(user ? 'member' : 'guest')
  // GA の user_id も Sentry と同じく認証状態が変わるたびに更新する
  updateAnalyticsUser(user?.uid ?? null)

  // NOTE:
  // 2回mountすると画面が真っ白になってしまう不具合の暫定的な修正
  // @see https://www.notion.so/b5a63782d69a45679f74882dab309a0d
  if (app) {
    return
  }
  app = createApp(NitteApp)

  addIntegration(vueIntegration({ app }))

  // 本番環境かつログインユーザーの場合のみLogRocketを初期化する。
  // ログイン後の遷移先に関わらず必ず記録されるようauth状態確定時に行う。
  // 初期バンドルに含めないよう動的importで読み込む。
  if (import.meta.env.MODE === 'production' && user) {
    import('logrocket').then(({ default: LogRocket }) => {
      LogRocket.init('1tyfyr/nitte', {
        network: {
          // LINEの長期トークンを操作記録に残さない。
          requestSanitizer: (request) =>
            request.url.includes('/line_integration') ? null : request
        }
      })
      LogRocket.identify(user.uid, {
        name: user.displayName || 'anonymous',
        email: user.email || 'anonymous'
      })
    })
  }

  // レイアウト
  app.component('StrongG', StrongG)
  app.component('MediumG', MediumG)
  app.component('WeakG', WeakG)
  app.component('StrongHr', StrongHr)
  app.component('MediumHr', MediumHr)

  // よく使うコンポーネント
  app.component('NitButton', NitButton)

  // テキスト @see フォントのルール
  // タイトル
  app.component('MainTitle', MainTitle)
  app.component('HeroTitle', HeroTitle)
  app.component('SubTitle', SubTitle)

  // ラベル、文章
  app.component('Description', Description)
  // Label(<label/>)のみmain-labelにもしたくないし、cssで定義している。

  // 補助ラベル、文章
  app.component('SupLabel', SupLabel)
  app.component('SupDescription', SupDescription)

  // Roboto
  app.component('Roboto', Roboto)
  app.component('SmallText', SmallText)

  // Spacer
  app.component('Spacer', Spacer)

  app.config.errorHandler = (err: unknown, _vm: any, _info: any) => {
    // エラーをSentryに送信（本番環境のみ、遅延ロード）
    captureException(err)

    // エラー用のモーダル
    // useNotificationStore().showNotification(
    //   'システムエラーが発生しました。すぐに調査させていただきます🙇‍♂️',
    //   NotificationType.Error
    // )
    throw err
  }

  app.directive('click-outside', clickOutside)

  app.directive('keydown', keydown)

  router.beforeEach((to, from) => {
    // スマホもしくはnoChannelが立っている場合チャネルトークを表示しない
    if (user && isDesktop() && !to.meta.noClack) {
      bootClack(user)
    }
  })

  const pinia = createPinia()
    .use(PiniaFirestoreSync)
    .use(createSentryPiniaPlugin())
  app.use(router).use(pinia).use(FloatingVue).mount('#app')

  // 埋め込みの場合
  if (isEmbed()) {
    document.getElementById('app')?.classList.add('embed')
  }

  // プレビューの場合
  if (isPreview()) {
    document.getElementById('app')?.classList.add('preview')
  }
})
